import { Category, Menu, MenuProduct, Product } from "@greeter/core";
import { assign, raise, setup } from "xstate";
import { produce } from "immer";
import {
  defined,
  insert,
  pushTo,
  toDefined,
  existsIn,
  unique,
  any,
} from "@greeter/array";
import { and, not, or } from "@greeter/func";
import { logger, warner } from "@greeter/log";
import { PriceChange } from "@greeter/core";
import * as SetUtil from "@greeter/set";

import { sortBy } from "lodash";
import { EventEmitter } from "@greeter/event";

const logName = "[EditMenuMachine]";
const warn = warner(logName);
const info = logger(logName);

/**
 * For assigning properties that are only truthy instead of
 * assigning properties that are also undefined.
 */
function assignValidProperties<T extends {}, B extends {}>(
  target: T,
  source: B
) {
  const truthyProperties = Object.fromEntries(
    Object.entries(source).filter(
      ([_, value]) =>
        value === "" || value === 0 || value !== undefined || value !== null
    )
  );
  Object.assign(target, truthyProperties);

  return target;
}

function updatePriority<T extends { priority?: number }>(t: T, index: number) {
  t.priority = index;

  return t;
}

/**
 * Checks whether the index was found in the array.
 *
 * It's is a simple check for whether the index is larger that -1.
 */
function foundIndex(idx: number) {
  return idx >= 0;
}

function toUpdatedFrom(updatedProducts: MenuProduct[]) {
  return (product: MenuProduct, ..._: unknown[]) => {
    const updated = updatedProducts.find((up) => up.id === product.id);
    return updated ? updated : product;
  };
}

export function byId<T extends { id: string }>(t: T | string | undefined) {
  return (other: string | T, ..._: unknown[]) =>
    typeof t !== "undefined" &&
    (typeof t === "string" ? t : t.id) ===
      (typeof other === "string" ? other : other.id);
}

export function applyChanges(menu: Menu, changes: Change[]): Menu {
  const log = logger("[applyChanges]");

  const next = produce(menu, (draft) => {
    for (const change of changes) {
      switch (change.op) {
        case "setFeaturedCategory":
          draft.featuredCategoryId = change.data.categoryId;
          break;

        case "addProduct": {
          // Could and should abstract out into a strategy pattern or something
          const products: MenuProduct[] = sortBy(
            draft.products,
            ...Product.sortByPriceAndPriorityProperties()
          ).filter(MenuProduct.filterByCategory(change.data.categoryId));

          if (draft.products.find((p) => p.id === change.data.product.id)) {
            return;
          }

          insert(products, change.data.position, {
            ...change.data.product,
            categories: [change.data.categoryId],
          });

          products.forEach(updatePriority);

          draft.products = draft.products.filter((d) => {
            return !products.find((p) => p.id === d.id);
          });

          products.forEach(pushTo(draft.products));
          break;
        }

        case "removeProduct": {
          draft.products = draft.products.filter(
            (p) => p.id !== change.data.productId
          );
          break;
        }
        case "moveProduct": {
          const { to, productId } = change.data;

          const productIdx = draft.products.findIndex(
            (p) => p.id === productId
          );
          const product = draft.products.splice(productIdx, 1)[0];

          if (!product) {
            warn(`Could not find product with id ${productId}`);
            return;
          }

          const destCategory = draft.categories.find(
            (c) => c.id === to.categoryId
          );

          const productsByCategory = draft.products.filter((p) =>
            p.categories.find((pc) => pc === product.categories[0])
          );

          let destProductIdx = productsByCategory.findIndex(
            (p) => p.id === to.productId
          );

          if (!destCategory) {
            warn(
              `Could not find category with id ${to.categoryId} in the "to" parameters`
            );
            return;
          }

          product.categories = [destCategory.id];
          insert(
            productsByCategory,
            to.direction === "before" ? destProductIdx : destProductIdx + 1,
            product
          );
          productsByCategory.forEach(updatePriority);
          productsByCategory.sort(
            ...Product.sortByPriceAndPriorityProperties()
          );

          draft.products = draft.products.filter((p) => {
            return !productsByCategory.find(byId(p));
          });
          productsByCategory.forEach(pushTo(draft.products));

          break;
        }
        case "editProduct":
          draft.products = draft.products.map((p) => {
            if (p.id === change.data.productId) {
              assignValidProperties(p, change.data.product);
            }

            return p;
          });
          break;

        case "removeCategory":
          draft.categories = draft.categories.filter(
            (c) => c.id !== change.data.categoryId
          );

          break;

        case "addCategory": {
          const category: Category = {
            id: window.crypto.randomUUID(),
            name: "",
            priority: 0,
          };
          log("Adding category with data", change.data);
          change.data.newCategoryId = category.id;
          if (!change.data.categoryId) {
            log(
              `No category provided, inserted '${JSON.stringify(
                category
              )}' at beginning`
            );
            draft.categories.unshift(category);
            return;
          }

          if (draft.categories.find((c) => c.id === category.id)) {
            log(`Unable to find category with id '${category.id}'`);
            return;
          }

          const idx = Math.max(
            draft.categories.findIndex((c) => c.id === change.data.categoryId),
            0
          );
          const insertIdx = change.data.direction === "before" ? idx : idx + 1;
          insert(draft.categories, insertIdx, category);
          log(
            `Found category at index '${idx}',` +
              `inserting at '${insertIdx}',` +
              `because it's '${change.data.direction}' '${idx}'`
          );
          draft.categories.forEach(updatePriority);

          break;
        }

        case "editCategory": {
          draft.categories = draft.categories.map((c) => {
            if (c.id === change.data.category.id) {
              assignValidProperties(c, change.data.category);
            }

            return c;
          });

          break;
        }
        case "moveCategory": {
          const destinationCategoryIdx = draft.categories.findIndex(
            byId(change.data.to.categoryId)
          );
          if (!foundIndex(destinationCategoryIdx)) {
            warn(
              "could not find 'to' category id with value " +
                change.data.to.categoryId
            );
            return;
          }
          const fromCategoryIdx = draft.categories.findIndex(
            byId(change.data.categoryId)
          );
          if (!foundIndex(fromCategoryIdx)) {
            warn(
              "could not find 'from' category id with value " +
                change.data.categoryId
            );
            return;
          }

          // Move the category
          //
          // We add 1 since if the move is "after" the category, splice requires
          // it to be the index higher. Since splice insertion with the same index
          // will result in the newly inserted to be inserted 'before' the
          // item with the same index.
          draft.categories.splice(
            destinationCategoryIdx +
              (change.data.to.direction === "after" ? 1 : 0),
            0,
            draft.categories[fromCategoryIdx]
          );

          // Remove the previous item
          //
          // We add 1 since if the move is "before" the category, the index
          // has shifted by 1. We use this knowledge to adjust the 'fromCategoryIdx'.
          draft.categories.splice(
            destinationCategoryIdx < fromCategoryIdx
              ? fromCategoryIdx + 1
              : fromCategoryIdx,
            1
          );

          // Update their priority in relation to eachother
          draft.categories.forEach(updatePriority);
        }
      }
    }
  });

  return next;
}

type InsertionPoint = "before" | "after";

export type AddProductChange = {
  op: "addProduct";
  data: {
    product: MenuProduct;
    categoryId: string;
    position: number;
  };
};

export type AddPriceChange = {
  op: "addPriceChange";
  data: {
    productId: string;
    priceChange: PriceChange;
  };
};

export type EditProductChange = {
  op: "editProduct";
  data: {
    productId: string;
    product: Partial<Omit<MenuProduct, "id">>;
  };
};

export type RemoveProductChange = {
  op: "removeProduct";
  data: {
    productId: string;
  };
};

export type AddCategoryChange = {
  op: "addCategory";
  data: {
    categoryId?: string;
    newCategoryId?: string;
    direction?: InsertionPoint;
  };
};

export type EditCategoryChange = {
  op: "editCategory";
  data: {
    category: Partial<Category>;
  };
};

export type RemoveCategoryChange = {
  op: "removeCategory";
  data: {
    categoryId: string;
  };
};

export type MoveCategoryChange = {
  op: "moveCategory";
  data: {
    categoryId: string;
    to: { direction: InsertionPoint; categoryId: string };
  };
};

type ProductPositionChange = {
  categoryId: string;
  productId?: string; // TODO: Should this be a "between" parameter instead?
  direction: InsertionPoint;
};
export type MoveProductChange = {
  op: "moveProduct";
  data: {
    productId: string;
    to: ProductPositionChange;
  };
};

type SetFeaturedCategoryChange = {
  op: "setFeaturedCategory";
  data: {
    categoryId: string;
  };
};

export type ProductChange =
  | AddProductChange
  | EditProductChange
  | RemoveProductChange
  | MoveProductChange;

export type CategoryChange =
  | AddCategoryChange
  | RemoveCategoryChange
  | EditCategoryChange
  | MoveCategoryChange
  | SetFeaturedCategoryChange;

export type Change = ProductChange | CategoryChange;

type UndoEvent = {
  type: "undo";
  data?: Change;
};

type UndoAllEvent = {
  type: "undoAll";
};

type UndoAllForProductEvent = {
  type: "undoAllForProduct";
  data: string;
};

type UndoForProductEvent = {
  type: "undoForProduct";
  data: string;
};

type UndoAllForCategoryEvent = {
  type: "undoAllForCategory";
  data: string;
};

type UndoForCategoryEvent = {
  type: "undoForCategory";
  data: string;
};

type SelectCategoryEvent = {
  type: "selectCategory";
  data: string;
};

type SelectProductEvent = {
  type: "selectProduct";
  data: string;
};

type MoveEvent = {
  type: "move";
};

type EditEvent = {
  type: "edit";
  data: Change;
};

type UpdateContextEvent = {
  type: "updateContext";
  data: Partial<Pick<EditMenuContext, "menu">>;
};

type RecalculateEvent = {
  type: "recalculate";
};

type MoveModeEvent = {
  type: "moveMode";
};

type PreviewModeEvent = {
  type: "previewMode";
};

type EditModeEvent = {
  type: "editMode";
};

type BackEvent = {
  type: "back";
};

type MainModeEvent = {
  type: "mainMode";
};

type NextEvent = {
  type: "next";
};

type DeselectAllEvent = {
  type: "deselectAll";
};

type ResetEvent = {
  type: "reset";
  data: Menu;
};

export type EditMenuEvents =
  | UndoEvent
  | UndoAllEvent
  | UndoAllForProductEvent
  | UndoForProductEvent
  | UndoAllForCategoryEvent
  | UndoForCategoryEvent
  | SelectProductEvent
  | DeselectAllEvent
  | EditEvent
  | UpdateContextEvent
  | RecalculateEvent
  | MainModeEvent
  | MoveModeEvent
  | PreviewModeEvent
  | EditModeEvent
  | SelectCategoryEvent
  | BackEvent
  | NextEvent
  | MoveEvent
  | ResetEvent;

type Error<T> = { code: T; reason: string };
type SelectError = Error<"select">;
function selectError(reason: string): SelectError {
  return { code: "select", reason: reason };
}
type Errors = SelectError;

function stringifyError<T>(err: Error<T>): string {
  return JSON.stringify(err, null, 2);
}

export function editProductMessage(data: EditProductChange["data"]): EditEvent {
  return {
    type: "edit",
    data: {
      op: "editProduct",
      data: data,
    },
  };
}

export function removeProductMessage(
  data: RemoveProductChange["data"]
): EditEvent {
  return {
    type: "edit",
    data: {
      op: "removeProduct",
      data: data,
    },
  };
}

export function editCategoryMessage(
  data: EditCategoryChange["data"]["category"]
): EditEvent {
  return {
    type: "edit",
    data: {
      op: "editCategory",
      data: {
        category: data,
      },
    },
  };
}

export function addProductMessage(data: AddProductChange["data"]): EditEvent {
  return {
    type: "edit",
    data: {
      op: "addProduct",
      data: data,
    },
  };
}

function toProductId(change: Change) {
  return change.op === "addProduct"
    ? change.data.product.id
    : change.op === "removeProduct"
    ? change.data.productId
    : change.op === "editProduct"
    ? change.data.productId
    : change.op === "moveProduct"
    ? change.data.productId
    : undefined;
}

function toCategoryId(change: Change) {
  return change.op === "addCategory"
    ? change.data.newCategoryId
    : change.op === "removeCategory"
    ? change.data.categoryId
    : change.op === "moveCategory"
    ? change.data.categoryId
    : change.op === "editCategory"
    ? change.data.category.id
    : undefined;
}

function addedProducts(change: Change, ..._: unknown[]) {
  return change.op === "addProduct";
}

function removed(change: Change, ..._: unknown[]) {
  return change.op === "removeProduct";
}

function edited(change: Change, ..._: unknown[]) {
  return change.op === "editProduct";
}

function movedProduct(change: Change, ..._: unknown[]) {
  return change.op === "moveProduct";
}

function movedCategory(change: Change, ..._: unknown[]) {
  return change.op === "moveCategory";
}

function addedCategory(change: Change, ..._: unknown[]) {
  return change.op === "addCategory";
}

function removedCategory(change: Change, ..._: unknown[]) {
  return change.op === "removeCategory";
}

function changedCategory(change: Change, ..._: unknown[]) {
  return change.op === "editCategory";
}

function setFeatured(change: Change, ..._: unknown[]) {
  return change.op === "setFeaturedCategory";
}

type ChangeSummary = {
  changedCategories: Set<string>;
  addedCategories: Set<string>;
  removedCategories: Set<string>;
  movedCategories: Set<string>;

  changedProducts: Set<string>;
  addedProducts: Set<string>;
  removedProducts: Set<string>;
  movedProducts: Set<string>;

  setFeatured: boolean;
};

export function summarizeChanges(changes: Change[]): ChangeSummary {
  const summary: ChangeSummary = {
    removedProducts: new Set(),
    addedCategories: new Set(),
    addedProducts: new Set(),
    changedCategories: new Set(),
    changedProducts: new Set(),
    movedCategories: new Set(),
    movedProducts: new Set(),
    removedCategories: new Set(),
    setFeatured: false,
  };

  // NOTE: We use a for loop as it optimizes time spent in loops.
  //       This for most changes results in one iteration through the changes,
  //       rather than the previous functional multi-iteration approach.
  for (let i = 0, l = changes.length; i < l; i++) {
    const change = changes[i];

    if (change.op === "removeProduct") {
      const id = toProductId(change);
      if (id) summary.removedProducts.add(id);
    } else if (change.op === "setFeaturedCategory") {
      summary.setFeatured = true;
    } else if (change.op === "addProduct") {
      const id = toProductId(change);
      if (id) summary.addedProducts.add(id);
    } else if (change.op === "addCategory") {
      const id = toCategoryId(change);
      if (id) summary.addedCategories.add(id);
    } else if (change.op === "editCategory") {
      const id = toCategoryId(change);
      if (id) summary.changedCategories.add(id);
    } else if (change.op === "editProduct") {
      const id = toProductId(change);
      if (
        id &&
        !summary.addedProducts.has(id) &&
        !summary.removedProducts.has(id) &&
        !SetUtil.intersection(
          summary.addedProducts,
          summary.removedProducts
        ).has(id)
      ) {
        summary.changedProducts.add(id);
      }
    } else if (change.op === "moveCategory") {
      const id = toCategoryId(change);
      if (id) summary.movedCategories.add(id);
    } else if (change.op === "moveProduct") {
      const id = toCategoryId(change);
      if (id) summary.movedProducts.add(id);
    } else if (change.op === "removeCategory") {
      const id = toCategoryId(change);
      if (id) summary.removedCategories.add(id);
    }
  }
  summary.addedProducts = SetUtil.difference(
    summary.addedProducts,
    summary.removedProducts
  );
  summary.removedProducts = SetUtil.difference(
    summary.removedProducts,
    summary.addedProducts
  );
  summary.addedCategories = SetUtil.difference(
    summary.addedCategories,
    summary.removedCategories
  );
  summary.removedCategories = SetUtil.difference(
    summary.removedCategories,
    summary.addedCategories
  );

  return summary;
}

/**
 * Filters all the edit actions for a specific product. Does not undo the add change.
 *
 * TODO: We might want to implement this as a stack pointer instead. This way we can 'redo' the changes.
 */
export function undoAllForProduct(
  changes: Change[],
  productId: string
): Change[] {
  const edits = changes.filter(
    (c) =>
      c.op !== "editProduct" ||
      (c.op === "editProduct" && c.data.productId !== productId)
  );

  return edits;
}

/**
 * Pops the first edit change for a product. It does this until there are no more undos.
 */
export function undoProduct(changes: Change[], productId: string): Change[] {
  const edits = changes.filter(
    (c) => c.op === "editProduct" && c.data.productId === productId
  );
  const edit = edits.pop();
  if (!edit) return changes;

  return changes.filter((c) => c !== edit);
}

export function undoCategory(changes: Change[], categoryId: string): Change[] {
  const edits = changes.filter(
    (c) => c.op === "editCategory" && c.data.category.id === categoryId
  );

  const edit = edits.pop();
  if (!edit) return changes;

  return changes.filter((c) => c !== edit);
}

export function undoAllCategory(
  changes: Change[],
  categoryId: string
): Change[] {
  const edits = changes.filter(
    (c) =>
      c.op !== "editCategory" ||
      (c.op === "editCategory" && c.data.category.id !== categoryId)
  );

  return edits;
}

/**
 * Creates a new array either with the item or without.
 *
 * This is compatible with React since we're
 */
export function toggle<T>(array: T[], t: T) {
  return array.find((c) => c === t)
    ? array.filter((c) => c !== t)
    : [...array, t];
}

export const editMenuMachineErrorEmitter = new EventEmitter<{
  event: "error";
  data: Errors;
}>();

editMenuMachineErrorEmitter.on("error", (error) => {
  warn(stringifyError(error.data));
});

type EditMenuContext = {
  applyIndex: number;
  changes: Change[];
  summary?: ChangeSummary;
  selectedCategories: string[];
  selectedProducts: string[];
  menu?: Menu;
  updatedMenu?: Menu;
};

export const editMenuMachine = setup({
  types: {} as {
    context: EditMenuContext;
    events: EditMenuEvents;
  },
}).createMachine({
  context: {
    applyIndex: 0,
    changes: [],
    summary: {
      addedCategories: new Set(),
      changedCategories: new Set(),
      removedCategories: new Set(),
      movedCategories: new Set(),

      addedProducts: new Set(),
      removedProducts: new Set(),
      changedProducts: new Set(),
      movedProducts: new Set(),
      setFeatured: false,
    },
    selectedCategories: [],
    selectedProducts: [],
  },

  initial: "initial",

  on: {
    /**
     * Recalculates the state of the updated menu.
     */
    recalculate: {
      actions: [
        assign(({ context: ctx }) => {
          if (!ctx.menu) return {};

          const unapplied = ctx.changes.slice(ctx.applyIndex);
          const startingPoint =
            ctx.updatedMenu && ctx.applyIndex > 0 ? ctx.updatedMenu : ctx.menu;
          const updated = applyChanges(startingPoint, unapplied);

          return { updatedMenu: updated, applyIndex: ctx.changes.length };
        }),
        assign({
          summary: ({ context: ctx }) => summarizeChanges(ctx.changes),
        }),
      ],
    },

    reset: {
      actions: [
        assign(({ event: ev }) => {
          return {
            menu: ev.data,
            selectedCategories: [],
            selectedProducts: [],
            changes: [],
            applyIndex: 0,
            updatedMenu: ev.data,
            summary: summarizeChanges([]),
          };
        }),
        raise({ type: "recalculate" }),
      ],
    },

    /**
     * Adds the changes to the product.
     */
    edit: {
      actions: [
        assign({
          changes: ({ context: ctx, event: ev }) => {
            return [...ctx.changes, ev.data];
          },
        }),
        raise({ type: "recalculate" }),
      ],
    },

    undo: {
      actions: [
        assign({
          changes: ({ context: ctx }) => [...ctx.changes.slice(0, -1)],
          applyIndex: 0,
        }),
        raise({ type: "recalculate" }),
      ],
    },

    undoAll: {
      actions: [
        assign({ changes: () => [], applyIndex: 0 }),
        raise({ type: "recalculate" }),
      ],
    },

    undoForProduct: {
      actions: [
        assign({
          changes: ({ context: ctx, event: ev }) => {
            return undoProduct(ctx.changes, ev.data);
          },
          applyIndex: 0,
        }),
        raise({ type: "recalculate" }),
      ],
    },

    undoAllForProduct: {
      actions: [
        assign({
          changes: ({ context: ctx, event: ev }) => {
            return undoAllForProduct(ctx.changes, ev.data);
          },
          applyIndex: 0,
        }),
        raise({ type: "recalculate" }),
      ],
    },

    undoForCategory: {
      actions: [
        assign({
          changes: ({ context: ctx, event: ev }) => {
            return undoCategory(ctx.changes, ev.data);
          },
          applyIndex: 0,
          updatedMenu: undefined,
        }),
        raise({ type: "recalculate" }),
      ],
    },

    undoAllForCategory: {
      actions: [
        assign({
          changes: ({ context: ctx, event: ev }) => {
            return undoAllCategory(ctx.changes, ev.data);
          },
          applyIndex: 0,
          updatedMenu: undefined,
        }),
        raise({ type: "recalculate" }),
      ],
    },

    selectCategory: {
      actions: [
        assign({
          selectedCategories: ({ context: ctx, event: ev }) => {
            if (
              !ctx.selectedCategories.find((sc) => sc === ev.data) &&
              ctx.selectedCategories.length + 1 === ctx.menu?.categories.length
            ) {
              editMenuMachineErrorEmitter.emit({
                event: "error",
                data: selectError(
                  `It does not make sense to select all categories for moving. Where would you move it? Tried to select ${ev.data}`
                ),
              });
              return ctx.selectedCategories;
            }
            return toggle(ctx.selectedCategories, ev.data);
          },
        }),
      ],
    },

    selectProduct: {
      actions: [
        assign({
          selectedProducts: ({ context: ctx, event: ev }) => {
            return toggle(ctx.selectedProducts, ev.data);
          },
        }),
      ],
    },

    deselectAll: {
      actions: [
        assign({
          selectedProducts: [],
          selectedCategories: [],
        }),
      ],
    },

    updateContext: {
      actions: [
        assign(({ context: ctx, event: ev }) => {
          return assignValidProperties(ctx, ev.data);
        }),
      ],
    },

    /**
     * No need to be strict about this.
     * Allow for movement freely between menus
     */
    back: { target: ".initial" },
    mainMode: { target: ".initial" },
    editMode: { target: ".edit" },
    moveMode: { target: ".move" },
    previewMode: { target: ".preview" },
  },
  states: {
    initial: {},
    edit: {},
    move: {
      on: {
        move: {
          target: "moving",
          guard: ({ context: ctx }) =>
            any(ctx.selectedCategories) || any(ctx.selectedProducts),
        },
      },
    },
    moving: {
      on: {
        next: {
          target: "move",
          actions: [
            assign({
              selectedCategories: () => [],
              selectedProducts: () => [],
            }),
          ],
        },
      },
    },
    preview: {},
  },
});
